import { otherEventTypeOption } from "./types";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";

/**
 * The app-constant form sections — deliberate, not residue (#60).
 *
 * Every discipline runs the same wizard: the entry-flow wording, step list,
 * documents-step copy, and the event-details/contact/venue steps are uniform
 * product copy, owned here in code. A profile row carries only what genuinely
 * varies per discipline (identity, content questions, entry-flow enabled
 * flags, initial form data); any stored copy of these constant sections is
 * ignored by parseInquiryProfileConfig — editing them here is the only way
 * they change. Backend-only keys nested under a constant section
 * (documentsStep.extraction) still ride along from the stored row.
 *
 * Which fields ask the core questions is the one list in Go
 * (`go-service/internal/profile/core.go`), and a test there fails if a
 * field it names leaves this file. The labels here are the screen's own.
 */

export const constantEntryFlowCopy: InquiryProfileConfig["entryFlow"] = {
	title: "Get a price for your event",
	description:
		"Tell us what you've got planned and we'll sort you out a quote.",
	quickEnquiry: {
		enabled: true,
		label: "Just ask a question",
		description:
			"No forms. Flick us a short message and we'll reply by email.",
	},
	fullBrief: {
		enabled: true,
		label: "Build my quote",
		description:
			"Answer a few quick questions and see your price range before you send. About 3 minutes.",
	},
	// Opt-in per Company (#136): the constant `enabled` here is never read —
	// parseInquiryProfileConfig always overrides it from stored config, and
	// an absent stored key means disabled.
	dryHire: {
		enabled: false,
		label: "Hire equipment only",
		description:
			"Know exactly what you need? Pick your gear and dates, and collect it yourself. No crew needed.",
	},
};

// Contact is deliberately last: the customer invests in describing the
// event before being asked for their details (higher completion, and the
// final step is the natural submit point).
export const constantSteps: InquiryProfileConfig["steps"] = [
	{ id: "tech-rider", title: "Documents", progressLabel: "Documents" },
	{ id: "event-details", title: "Event Details", progressLabel: "Event" },
	{ id: "venue", title: "Venue & Logistics", progressLabel: "Venue" },
	{ id: "content", title: "Content & Setup", progressLabel: "Content" },
	{ id: "contact", title: "Contact Information", progressLabel: "Contact" },
];

export const constantDocumentsStep: InquiryProfileConfig["documentsStep"] = {
	title: "Got any event documents?",
	description:
		"A tech rider, timetable, or run sheet. We'll pull out the details so you don't have to type them.",
	yesLabel: "Yes, I have one",
	noLabel: "No, I don't",
	uploadAriaLabel: "Upload tech rider file",
	uploadPrompt: "Drag & drop or click to upload",
	acceptedFormatsLabel: "PDF, DOC, or DOCX (max 10MB)",
	missingFileMessage:
		'Upload a file or choose "No, I don\'t" to continue without one',
	parseFailedTitle: "We couldn't quite read that file.",
	parseFailedMessage:
		"No worries. Carry on and we'll ask you the details in the next steps.",
	noDocumentMessage:
		"No problem! We'll ask you about your audio requirements in the next steps.",
};

export const constantEventDetailsStep: InquiryProfileConfig["eventDetailsStep"] =
	{
		title: "What's the occasion?",
		fields: {
			eventType: {
				field: "eventType",
				label: "What kind of event is it?",
				placeholder: "Select event type...",
			},
			eventTypeOther: { field: "eventTypeOther", label: "Please specify" },
			eventName: { field: "eventName", label: "What's the event called?" },
			organization: {
				field: "organization",
				label: "Organisation",
				optional: true,
			},
			eventDate: { field: "eventDate", label: "Start date" },
			eventEndDate: { field: "eventEndDate", label: "End date", optional: true },
			eventStartTime: {
				field: "eventStartTime",
				label: "Start time",
				optional: true,
			},
			eventEndTime: {
				field: "eventEndTime",
				label: "Finish time",
				optional: true,
			},
		},
		// The Profile's own types replace this when the config is parsed
		// (#197); Other alone is what a config that carries none offers.
		eventTypeOptions: [otherEventTypeOption],
	};

export const constantContactStep: InquiryProfileConfig["contactStep"] = {
	title: "Where should we send your quote?",
	fields: {
		contactName: { field: "contactName", label: "Your name" },
		contactEmail: { field: "contactEmail", label: "Email" },
		contactPhone: { field: "contactPhone", label: "Phone" },
		preferredContactMethod: {
			field: "preferredContactMethod",
			label: "How should we get back to you?",
		},
		// Asked only for a call or a text: a time to email somebody is
		// not an answer to anything.
		contactWhen: {
			field: "contactWhen",
			label: "When suits?",
			optional: true,
			hint: "Anytime, lunchtimes, after 5pm, whatever works for you.",
		},
	},
};

export const constantVenueStep: InquiryProfileConfig["venueStep"] = {
	title: "Where's it happening?",
	scale: {
		field: "attendance",
		label: "Audience numbers",
		emptyMessage: "Select an audience size or enter a number",
		invalidMessage: "Enter a valid audience number",
	},
	fields: {
		location: { field: "location", label: "Venue location" },
		venueContact: { field: "venueContact", label: "Venue contact", optional: true },
		environment: {
			label: "Indoors or outdoors?",
			// "Not sure yet" is a first-class answer — outdoor-only follow-ups
			// stay hidden (they key on `equals: "Outdoor"`).
			options: ["Indoor", "Outdoor", "Not sure yet"],
			emptyMessage: "Pick one, 'Not sure yet' is fine too",
		},
	},
	outdoor: {
		when: { field: "indoorOutdoor", equals: "Outdoor" },
		powerAccess: {
			field: "powerAccess",
			label: "Power Access",
			emptyMessage: "This field is required for outdoor events",
		},
		wetWeatherPlan: { field: "wetWeatherPlan", label: "Wet Weather Plan" },
	},
	stage: {
		available: {
			// "existing" matters: on a staging profile the customer is hiring a
			// stage FROM the company — this asks about what the venue already has.
			field: "hasStage",
			label: "Is there an existing stage at the venue?",
		},
		details: { field: "stageDetails", label: "Stage details" },
	},
	siteVisit: {
		// Every rental company can send someone out to look, so the offer is
		// on the shared step for every discipline rather than in one
		// Profile's pack (DESIGN.md, "The core steps ask only what every
		// rental equipment company can ask"). The Receptionist makes the
		// same offer on every call.
		available: {
			field: "siteVisit",
			label: "Site visit before we quote",
			description:
				"Someone from the team comes out to look at access, distances, and where the gear would sit.",
		},
		when: {
			field: "siteVisitWhen",
			label: "What day and roughly what time would suit?",
			hint: "A rough window is fine.",
		},
	},
	timing: {
		title: "Setup Timing (Optional)",
		description:
			"Help us plan crew schedules. You can fill these in now or we'll confirm later.",
		invalidTimeMessage: "Enter a valid time (e.g. 8:00 AM) or leave blank",
		siteAvailableFrom: {
			field: "siteAvailableFrom",
			label: "Venue open",
			hint: "When venue opens for setup",
		},
		packOutTime: {
			field: "packOutTime",
			label: "Pack out",
			hint: "When we need to leave",
		},
	},
};
